/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { EMAIL, LAST_NAMES, NAME, PHONE_NUMBER } from "@utils/expreciones";
import { useFormik } from "formik";
import * as yup from "yup";
import CustomContryCode from "@components/CustomContryCode/CustomContryCode";
import { ApiError, PersonaStructure } from "@interfaces/response";
import { PayloadPersonPatch } from "@interfaces/request";
import { RootState, useAppDispatch } from "@store/store";
import { useLazyGetPersonByIdQuery, usePatchPersonMutation } from "@api/personasApi";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { InactivityHandler } from "@components/InactivityHandler";
import CustomRightImage from "@components/CustomRightImagev2";
import * as Sentry from "@sentry/react";
import useFormikErrors from "@hooks/useFormikErrors";
import { useLazyGetStatesQuery } from "@api/catalogs";
import { StateStructure } from "@interfaces/catalogs";
import CustomRadioGroup from "@components/CustomRadioGroup";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";

interface AvalAdicionalInfoForm {
  name: string;
  last_name: string;
  last_name_2: string;
  birth_date: string;
  birth_state: string;
  email: string;
  country_code: string;
  phone: string;
  sex: string;
}

const MAX_DATE = dayjs().subtract(18, "years");

const InformacionAvalAdicionalP1 = () => {
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const avalAdicionalId = useSelector((state: RootState) => state.bofu.aval_adicional_id_information);

  const [patchPerson] = usePatchPersonMutation();
  const [getPersonById] = useLazyGetPersonByIdQuery();
  const [getEstados] = useLazyGetStatesQuery();

  const [isLoading, setIsLoading] = useState(false);
  const [avalSelected, setAvalSelected] = useState<PersonaStructure | null>(null);
  const [listaEstados, setListaEstados] = useState<StateStructure[]>([]);

  const formik = useFormik<AvalAdicionalInfoForm>({
    initialValues: {
      name: "",
      last_name: "",
      last_name_2: "",
      birth_date: "",
      birth_state: "",
      email: "",
      country_code: "+52",
      phone: "",
      sex: "",
    },
    validationSchema: yup.object().shape({
      name: yup.string().required("Campo requerido").matches(new RegExp(NAME), "Nombre inválido, revisa el formato"),
      last_name: yup
        .string()
        .required("Campo requerido")
        .matches(new RegExp(LAST_NAMES), "Apellido paterno inválido, revisa el formato"),
      last_name_2: yup.string().matches(new RegExp(LAST_NAMES), "Apellido materno inválido, revisa el formato"),
      birth_date: yup
        .string()
        .required("Campo requerido")
        .matches(/^\d{4}-\d{2}-\d{2}$/, "Formato de fecha no válido")
        .test("is-adult", "Debes ser mayor de 18 años", (value) => {
          if (!value) return false;

          const birthDate = dayjs(value);
          const age = dayjs().diff(birthDate, "years");

          return age >= 18;
        }),
      birth_state: yup.string().required("Campo requerido"),
      email: yup.string().required("Campo requerido").matches(EMAIL, "Correo electrónico inválido"),
      country_code: yup.string().required("Campo requerido"),
      phone: yup.string().required("Campo requerido").matches(PHONE_NUMBER, "Número de teléfono inválido"),
      sex: yup.string().required("Campo requerido"),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        if (!avalSelected) {
          return;
        }

        setSubmitting(true);

        const payload: PayloadPersonPatch = {
          name: values.name,
          last_name: values.last_name,
          last_name_2: values.last_name_2,
          birth_date: values.birth_date,
          birth_state: +values.birth_state,
          email: values.email,
          code_phone_number: values.country_code,
          phone_number: values.phone,
          sex: values.sex,
        };

        await patchPerson({
          personId: avalSelected.id,
          body: payload,
        }).unwrap();

        navigate("/producto/creditosimple/aval_adicional_buro_2");
      } catch (error) {
        const api_error = error as ApiError;

        if (api_error.status === 409) {
          formik.setFieldError("phone", "El número de teléfono ya se encuentra registrado para otro aval");
          return;
        }

        Sentry.captureException(error, {
          extra: {
            component: "AvalAdicionalBuroP1-submit",
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setSubmitting(false);
      }
    },
    validateOnMount: true,
  });

  const { validateMultipleFields } = useFormikErrors({ formik });

  const savePersonData = async (
    e:
      | React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
      | React.SyntheticEvent<Element, Event>,
  ) => {
    try {
      formik.handleBlur(e);

      if (!avalSelected) {
        return;
      }

      const isValid = validateMultipleFields([
        "name",
        "last_name",
        "last_name_2",
        "birth_date",
        "birth_state",
        "email",
        "country_code",
        "phone",
        "sex",
      ]);
      if (!isValid) {
        return;
      }

      const payload: PayloadPersonPatch = {
        name: formik.values.name,
        last_name: formik.values.last_name,
        last_name_2: formik.values.last_name_2,
        birth_date: formik.values.birth_date,
        birth_state: +formik.values.birth_state,
        email: formik.values.email,
        code_phone_number: formik.values.country_code,
        phone_number: formik.values.phone,
        sex: formik.values.sex,
      };

      await patchPerson({
        personId: avalSelected?.id,
        body: payload,
      }).unwrap();
    } catch (error) {
      console.error("Error guardando información personal:", error);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);

        if (!avalAdicionalId) {
          navigate("/producto/creditosimple/docs_aval");
          return;
        }

        const [aval_selected, res_estados] = await Promise.all([
          getPersonById(avalAdicionalId).unwrap(),
          getEstados().unwrap(),
        ]);

        // validar si el aval seleccionado existe
        if (!aval_selected) {
          navigate("/producto/creditosimple/docs_aval");
          return;
        }

        setAvalSelected(aval_selected);
        setListaEstados(res_estados);

        formik.setValues({
          name: aval_selected.name || "",
          last_name: aval_selected.last_name || "",
          last_name_2: aval_selected.last_name_2 || "",
          birth_date: dayjs(aval_selected.birth_date).format("YYYY-MM-DD") || "",
          birth_state: (aval_selected.birth_state || "").toString(),
          email: aval_selected.email || "",
          country_code: aval_selected.code_phone_number || "+52",
          phone: aval_selected.phone_number || "",
          sex: aval_selected.sex || "",
        });

        setTimeout(() => {
          formik.validateForm();
        }, 200);
      } catch (error) {
        const api_error = error as ApiError;
        Sentry.captureException(error, {
          extra: {
            component: "AvalAdicionalBuroP1-initial_fetch",
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8}>
        <InactivityHandler />
        <Box maxWidth="sm" mx="auto" mt={4} px={2}>
          <Typography
            textAlign="center"
            variant={isMobileOrTablet ? "body2" : "body1"}
            fontWeight={700}
            color={theme?.palette?.primary?.dark}
          >
            Información del aval
          </Typography>

          <Typography
            textAlign={"center"}
            color={theme?.palette?.primary?.main}
            variant={isMobileOrTablet ? "body2" : "body1"}
            fontWeight={600}
            mt={2}
          >
            Te solicitamos ingresar un segundo aval a tu solicitud.
          </Typography>

          <Typography
            mt={2}
            textAlign="center"
            variant={isMobileOrTablet ? "body2" : "body1"}
            color={theme?.palette?.primary?.main}
            fontWeight={600}
          >
            {avalSelected?.name} {avalSelected?.last_name} {avalSelected?.last_name_2}
          </Typography>

          <form onSubmit={formik.handleSubmit} noValidate>
            <FormControl fullWidth>
              <TextField
                id="name"
                name="name"
                label="Nombre(s)"
                margin="normal"
                required
                fullWidth
                autoComplete="off"
                onBlur={(e) => savePersonData(e)}
                onChange={formik.handleChange}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
                value={formik.values.name}
                disabled={isLoading || formik.isSubmitting}
              />
            </FormControl>

            <FormControl fullWidth>
              <TextField
                id="last_name"
                name="last_name"
                label="Apellido paterno"
                margin="normal"
                required
                fullWidth
                autoComplete="off"
                onBlur={(e) => savePersonData(e)}
                onChange={formik.handleChange}
                error={formik.touched.last_name && Boolean(formik.errors.last_name)}
                helperText={formik.touched.last_name && formik.errors.last_name}
                value={formik.values.last_name}
                disabled={isLoading || formik.isSubmitting}
              />
            </FormControl>

            <FormControl fullWidth>
              <TextField
                id="last_name_2"
                name="last_name_2"
                label="Apellido materno"
                margin="normal"
                required
                fullWidth
                autoComplete="off"
                onBlur={(e) => savePersonData(e)}
                onChange={formik.handleChange}
                error={formik.touched.last_name_2 && Boolean(formik.errors.last_name_2)}
                helperText={formik.touched.last_name_2 && formik.errors.last_name_2}
                value={formik.values.last_name_2}
                disabled={isLoading || formik.isSubmitting}
              />
            </FormControl>

            <FormControl fullWidth sx={{ mt: 2 }}>
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
                <DatePicker
                  label="Fecha de nacimiento"
                  value={dayjs(formik.values.birth_date)}
                  onChange={(date) => {
                    const formatted_date = dayjs(date).format("YYYY-MM-DD");
                    formik.setFieldValue("birth_date", formatted_date);
                  }}
                  maxDate={MAX_DATE}
                  slotProps={{
                    textField: {
                      name: "birth_date",
                      onBlur: formik.handleBlur,
                      error: formik.touched.birth_date && Boolean(formik.errors.birth_date),
                      helperText: formik.touched.birth_date ? formik.errors.birth_date : "",
                      required: true,
                    },
                  }}
                  disabled={isLoading || formik.isSubmitting}
                />
              </LocalizationProvider>
            </FormControl>

            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel id="birth_state">Lugar de nacimiento</InputLabel>
              <Select
                labelId="birth_state"
                id="birth_state"
                name="birth_state"
                fullWidth
                required
                label="Lugar de nacimiento"
                value={formik.values.birth_state}
                onBlur={savePersonData}
                onChange={formik.handleChange}
                error={formik.touched.birth_state && Boolean(formik.errors.birth_state)}
                disabled={isLoading || formik.isSubmitting}
              >
                <MenuItem value={""}>Selecciona</MenuItem>
                {listaEstados?.map((item) => (
                  <MenuItem key={item.id} value={item.id.toString()}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.birth_state && formik.errors.birth_state && (
                <FormHelperText>{formik.errors.birth_state}</FormHelperText>
              )}
            </FormControl>

            <FormControl fullWidth>
              <TextField
                id="email"
                name="email"
                label="Correo electrónico"
                margin="normal"
                required
                fullWidth
                autoComplete="off"
                onBlur={(e) => savePersonData(e)}
                onChange={(e) => {
                  const value_to_set = (e.target.value || "").trim().toLowerCase();
                  formik.setFieldValue("email", value_to_set);
                }}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                value={formik.values.email}
                disabled={isLoading || formik.isSubmitting}
                inputProps={{
                  maxLength: 100,
                }}
              />
            </FormControl>

            <Grid container spacing={1} mt={1}>
              <Grid item xs={4} sm={4} md={4}>
                <CustomContryCode
                  label="Código"
                  name="country_code"
                  value={formik.values.country_code}
                  onChange={formik.handleChange}
                  onBlur={(e) => savePersonData(e)}
                  error={formik.touched.country_code && Boolean(formik.errors.country_code)}
                  disabled={isLoading || formik.isSubmitting}
                />
              </Grid>

              <Grid item xs={8} sm={8} md={8}>
                <TextField
                  type="text"
                  name="phone"
                  variant="outlined"
                  label="Teléfono"
                  fullWidth
                  required
                  value={formik.values.phone}
                  onChange={(event) => {
                    formik.setFieldValue("phone", event.target.value.replace(/[^0-9]/g, ""));
                  }}
                  onBlur={(e) => savePersonData(e)}
                  error={formik.touched.phone && Boolean(formik.errors.phone)}
                  helperText={formik.touched.phone && formik.errors.phone}
                  inputProps={{
                    maxLength: 10,
                    inputMode: "numeric",
                  }}
                  disabled={isLoading || formik.isSubmitting}
                />
              </Grid>
            </Grid>

            <FormControl fullWidth sx={{ mt: 2 }}>
              <CustomRadioGroup
                label="Sexo como aparece en la INE"
                name="sex"
                value={formik.values.sex}
                onChange={formik.handleChange}
                onBlur={savePersonData}
                error={formik.touched.sex && Boolean(formik.errors.sex)}
                helperText={formik.touched.sex && formik.errors.sex}
                disabled={isLoading || formik.isSubmitting}
                row={true}
                options={[
                  {
                    label: "Masculino",
                    value: "MASCULINO",
                  },
                  {
                    label: "Femenino",
                    value: "FEMENINO",
                  },
                ]}
              />
            </FormControl>

            <Grid container spacing={1} my={1}>
              <Grid item xs={6} sm={6} md={6}>
                <Button
                  variant="blue_outlined"
                  type="button"
                  fullWidth
                  onClick={() => {
                    navigate("/producto/creditosimple/docs_aval");
                  }}
                  disabled={isLoading || formik.isSubmitting}
                >
                  Regresar al inicio
                </Button>
              </Grid>

              <Grid item xs={6} sm={6} md={6}>
                <Button
                  variant="default"
                  type="submit"
                  fullWidth
                  disabled={isLoading || !formik.isValid || formik.isSubmitting}
                >
                  Continuar
                </Button>
              </Grid>
            </Grid>
          </form>
        </Box>
      </Grid>

      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default InformacionAvalAdicionalP1;
